import { supabase } from '../lib/supabase';

function isSchemaMissingColumnError(err: { code?: string } | null): boolean {
  return err?.code === 'PGRST204' || err?.code === '42703';
}

/** PostgREST: Could not find the 'html_content' column of 'email_outbox' */
function parseMissingColumnName(message: string): string | null {
  const m = message.match(/Could not find the '([^']+)' column/i);
  return m ? m[1] : null;
}

/** Postgres 23502: null value in column "to_email" ... violates not-null constraint */
function parseNotNullColumnName(message: string): string | null {
  const m = message.match(/null value in column "([^"]+)"/i);
  return m ? m[1] : null;
}

function parseEmailList(s: string | undefined): string[] {
  if (!s?.trim()) return [];
  return s.split(/[,;\n]+/).map((x) => x.trim()).filter(Boolean);
}

function viteEnv(key: string): string {
  return ((import.meta as unknown as { env: Record<string, string | undefined> }).env[key])?.trim() || '';
}

/** Sender / CC / test routing: Vite env first (build-time), then email_automation_settings */
function envTransactionalSenderEmail(): string {
  return viteEnv('VITE_EMAIL_SENDER_EMAIL');
}

function envTransactionalSenderName(): string {
  return viteEnv('VITE_EMAIL_SENDER_NAME');
}

function envCampaignSenderEmail(): string {
  return viteEnv('VITE_EMAIL_CAMPAIGN_SENDER_EMAIL') || envTransactionalSenderEmail();
}

function envCampaignSenderName(): string {
  return viteEnv('VITE_EMAIL_CAMPAIGN_SENDER_NAME') || envTransactionalSenderName();
}

function envCcList(): string[] {
  return parseEmailList(viteEnv('VITE_EMAIL_CC'));
}

function envBccList(): string[] {
  return parseEmailList(viteEnv('VITE_EMAIL_BCC'));
}

function envTestRecipient(): string {
  return viteEnv('VITE_EMAIL_TEST_ADDRESS');
}

export interface EmailOutboxEntry {
  id: string;
  recipient_email: string;
  recipient_name?: string;
  sender_email: string;
  sender_name: string;
  subject: string;
  html_content: string;
  text_content?: string;
  status: 'pending' | 'sent' | 'failed' | 'cancelled';
  email_type: string;
  is_test_email: boolean;
  retries: number;
  max_retries: number;
  /** Real email_outbox column. */
  error_message?: string | null;
  /** Legacy name; not a real email_outbox column. */
  last_error?: string;
  metadata: any;
  related_booking_id?: string;
  related_customer_id?: string;
  related_reminder_id?: string;
  scheduled_for: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
  editable_until?: string;
  brevo_message_id?: string;
}

export interface EmailSettings {
  test_mode_enabled: boolean;
  test_email_address: string;
  default_sender_email: string;
  default_sender_name: string;
  booking_sender_email: string;
  booking_sender_name: string;
  campaign_sender_email: string;
  campaign_sender_name: string;
  additional_recipients: string;
  cc_recipients: string;
  bcc_recipients: string;
  portal_links_enabled?: boolean; // Feature flag for portal links
  auto_send_enabled?: boolean; // Toggle between automatic (15-min delay) and manual sending
}

export class EmailAutomationService {
  /**
   * Get email automation settings
   */
  static async getSettings(): Promise<Partial<EmailSettings>> {
    try {
      const { data, error } = await supabase
        .from('email_automation_settings')
        .select('setting_key, setting_value');

      if (error) {
        console.error('Error fetching email settings:', error);
        return {};
      }

      const settings: Partial<EmailSettings> = {};
      (data || []).forEach((item: any) => {
        const key = item.setting_key as keyof EmailSettings;
        const value = item.setting_value;
        
        // Boolean settings
        if (key === 'test_mode_enabled' || key === 'portal_links_enabled' || key === 'auto_send_enabled') {
          settings[key] = value === 'true';
        } else {
          (settings as any)[key] = value;
        }
      });

      return settings;
    } catch (error) {
      console.error('Error in getSettings:', error);
      return {};
    }
  }

  /**
   * Update email automation settings
   */
  static async updateSettings(settings: Partial<EmailSettings>): Promise<void> {
    try {
      const updates = Object.entries(settings).map(([key, value]) => ({
        setting_key: key,
        setting_value: typeof value === 'boolean' ? value.toString() : value,
        updated_at: new Date().toISOString()
      }));

      // Upsert each setting
      for (const update of updates) {
        const { error } = await supabase
          .from('email_automation_settings')
          .upsert(update, { onConflict: 'setting_key' });

        if (error) {
          console.error('Error updating setting:', update.setting_key, error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Error in updateSettings:', error);
      throw error;
    }
  }

  /**
   * Get email outbox entries with pagination
   */
  static async getEmailOutbox(limit: number = 50, offset: number = 0): Promise<EmailOutboxEntry[]> {
    try {
      const { data, error } = await supabase
        .from('email_outbox')
        .select('*')
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) {
        console.error('Error fetching email outbox:', error);
        throw error; // Re-throw to be caught by component
      }

      return data || [];
    } catch (error) {
      console.error('Error in getEmailOutbox:', error);
      throw error; // Re-throw to be caught by component
    }
  }

  /**
   * Get total count of emails in outbox
   */
  static async getEmailOutboxCount(): Promise<number> {
    try {
      const { count, error } = await supabase
        .from('email_outbox')
        .select('*', { count: 'exact', head: true });

      if (error) {
        console.error('Error fetching email outbox count:', error);
        throw error; // Re-throw to be caught by component
      }

      return count || 0;
    } catch (error) {
      console.error('Error in getEmailOutboxCount:', error);
      throw error; // Re-throw to be caught by component
    }
  }

  /**
   * Queue an email for sending
   */
  static async queueEmail(emailData: {
    recipient_email: string;
    recipient_name?: string;
    subject: string;
    html_content: string;
    text_content?: string;
    email_type: string;
    metadata?: any;
    related_booking_id?: string;
    related_customer_id?: string;
    related_reminder_id?: string;
    related_quote_id?: string;
    scheduled_for?: string;
    sender_email?: string;
    sender_name?: string;
    cc_emails?: string[]; // Allow override of CC emails if needed
    bcc_emails?: string[]; // Allow override of BCC emails if needed
  }): Promise<string> {
    try {
      // Get current settings to determine sender and test mode
      const settings = await this.getSettings();
      
      // Choose defaults by type; avoid outdated fallback address
      let senderEmail = emailData.sender_email || '';
      let senderName = emailData.sender_name || '';
      let recipientEmail = emailData.recipient_email;
      
      // Check if test mode is enabled
      const isTestMode = settings.test_mode_enabled === true; // Default to false if not set
      
      // Sender: explicit per-call → env (VITE_*) → Supabase email_automation_settings
      if (emailData.email_type === 'bulk_campaign') {
        senderEmail =
          senderEmail ||
          envCampaignSenderEmail() ||
          settings.campaign_sender_email ||
          settings.default_sender_email ||
          '';
        senderName =
          senderName ||
          envCampaignSenderName() ||
          settings.campaign_sender_name ||
          settings.default_sender_name ||
          '';
      } else {
        senderEmail =
          senderEmail ||
          envTransactionalSenderEmail() ||
          settings.booking_sender_email ||
          settings.default_sender_email ||
          '';
        senderName =
          senderName ||
          envTransactionalSenderName() ||
          settings.booking_sender_name ||
          settings.default_sender_name ||
          '';
      }

      // Test mode: redirect To — VITE_EMAIL_TEST_ADDRESS wins, then DB
      if (isTestMode) {
        const testTo = envTestRecipient() || settings.test_email_address?.trim();
        if (testTo) recipientEmail = testTo;
      }

      // CC/BCC: env lists if set, otherwise DB (comma-separated in settings)
      const isBulkCampaign = emailData.email_type === 'bulk_campaign';
      const envCc = envCcList();
      const envBcc = envBccList();
      const defaultCCEmails = envCc.length > 0 ? envCc : parseEmailList(settings.cc_recipients);
      const defaultBCCEmails = envBcc.length > 0 ? envBcc : parseEmailList(settings.bcc_recipients);
      const ccEmails = isBulkCampaign ? [] : (emailData.cc_emails ?? defaultCCEmails);
      const bccEmails = isBulkCampaign ? [] : (emailData.bcc_emails ?? defaultBCCEmails);

      // Don't modify the subject or content with test indicators when not in test mode
      let finalSubject = emailData.subject;
      let finalHtmlContent = emailData.html_content;
      let finalTextContent = emailData.text_content;

      // Capture user who initiated this email (for display in Ops Day Planner etc.)
      let initiatedByUserId: string | undefined;
      let initiatedByName: string | undefined;
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user?.id) {
          initiatedByUserId = user.id;
          initiatedByName =
            (user.user_metadata?.first_name && user.user_metadata?.last_name)
              ? `${user.user_metadata.first_name} ${user.user_metadata.last_name}`.trim()
              : (user.email?.split('@')[0] || undefined);
        }
      } catch (_) {
        // ignore
      }

      // Add CC emails and initiator to metadata for the email processor / UI
      const enhancedMetadata = {
        ...(emailData.metadata || {}),
        cc_emails: isBulkCampaign ? [] : ccEmails,
        bcc_emails: isBulkCampaign ? [] : bccEmails,
        original_cc_emails: isBulkCampaign ? [] : defaultCCEmails,
        original_bcc_emails: isBulkCampaign ? [] : defaultBCCEmails,
        ...(initiatedByUserId && { initiated_by_user_id: initiatedByUserId }),
        ...(initiatedByName && { initiated_by_name: initiatedByName })
      };

      const insertRow: Record<string, unknown> = {
        recipient_email: recipientEmail,
        recipient_name: emailData.recipient_name,
        sender_email: senderEmail,
        sender_name: senderName,
        // Legacy / alternate outbox schemas (NOT NULL to_email / from_email)
        to_email: recipientEmail,
        from_email: senderEmail,
        subject: finalSubject,
        html_content: finalHtmlContent,
        text_content: finalTextContent,
        email_type: emailData.email_type,
        is_test_email: isTestMode,
        metadata: enhancedMetadata,
        related_booking_id: emailData.related_booking_id,
        related_customer_id: emailData.related_customer_id,
        related_reminder_id: emailData.related_reminder_id,
        related_quote_id: emailData.related_quote_id,
        scheduled_for: emailData.scheduled_for || new Date().toISOString(),
        status: 'pending',
        retries: 0,
        max_retries: 3
      };

      let row: Record<string, unknown> = { ...insertRow };
      let result: { id: string } | null = null;
      let error: { code?: string; message?: string } | null = null;

      for (let attempt = 0; attempt < 28; attempt++) {
        const res = await supabase.from('email_outbox').insert(row).select('id').single();
        result = res.data as { id: string } | null;
        error = res.error as { code?: string; message?: string } | null;
        if (!error) break;

        // Legacy schemas: NOT NULL on to_email / from_email while app used recipient_email / sender_email only
        if (error.code === '23502' && error.message) {
          const nn = parseNotNullColumnName(error.message);
          if (nn === 'to_email' && recipientEmail) {
            row = { ...row, to_email: recipientEmail };
            continue;
          }
          if (nn === 'from_email' && senderEmail) {
            row = { ...row, from_email: senderEmail };
            continue;
          }
        }

        if (!isSchemaMissingColumnError(error) || !error.message) {
          break;
        }

        const missingCol = parseMissingColumnName(error.message);
        if (!missingCol || !(missingCol in row)) {
          break;
        }

        if (missingCol === 'recipient_email' || missingCol === 'subject') {
          break;
        }

        if (missingCol === 'metadata') {
          console.error(
            'email_outbox.metadata column missing; run supabase migrations (20250921111548_jade_shore / 20260406231000_email_outbox_standard_columns.sql)'
          );
          break;
        }

        const moved = row[missingCol];
        const { [missingCol]: _, ...rest } = row;
        const prevMeta =
          rest.metadata && typeof rest.metadata === 'object' && !Array.isArray(rest.metadata)
            ? (rest.metadata as Record<string, unknown>)
            : {};
        row = {
          ...rest,
          metadata: {
            ...prevMeta,
            [missingCol]: moved
          }
        };
      }

      if (error) {
        console.error('Error queueing email:', error);
        throw error;
      }

      return result!.id;
    } catch (error) {
      console.error('Error in queueEmail:', error);
      throw error;
    }
  }

  /**
   * Queue booking confirmation email
   */
  static async queueBookingConfirmation(
    customerId: string,
    bookingId: string,
    recipientEmail: string,
    recipientName: string,
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<string> {
    return this.queueEmail({
      recipient_email: recipientEmail,
      recipient_name: recipientName,
      subject,
      html_content: htmlContent,
      text_content: textContent,
      email_type: 'booking_confirmation',
      related_booking_id: bookingId,
      related_customer_id: customerId,
      metadata: {
        booking_id: bookingId,
        customer_id: customerId
      }
    });
  }

  /**
   * Queue instructor assignment email
   */
  static async queueInstructorAssignment(
    instructorEmail: string,
    instructorName: string,
    bookingId: string,
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<string> {
    return this.queueEmail({
      recipient_email: instructorEmail,
      recipient_name: instructorName,
      subject,
      html_content: htmlContent,
      text_content: textContent,
      email_type: 'instructor_assignment',
      related_booking_id: bookingId,
      metadata: {
        booking_id: bookingId,
        instructor_email: instructorEmail
      }
    });
  }

  /**
   * Queue certificate reminder email
   */
  static async queueCertificateReminder(
    customerId: string,
    reminderId: string,
    recipientEmail: string,
    recipientName: string,
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<string> {
    return this.queueEmail({
      recipient_email: recipientEmail,
      recipient_name: recipientName,
      subject,
      html_content: htmlContent,
      text_content: textContent,
      email_type: 'certificate_reminder',
      related_customer_id: customerId,
      related_reminder_id: reminderId,
      metadata: {
        customer_id: customerId,
        reminder_id: reminderId
      }
    });
  }

  /**
   * Queue review request email
   */
  static async queueReviewRequest(
    customerId: string,
    recipientEmail: string,
    recipientName: string,
    subject: string,
    htmlContent: string,
    textContent?: string
  ): Promise<string> {
    return this.queueEmail({
      recipient_email: recipientEmail,
      recipient_name: recipientName,
      subject,
      html_content: htmlContent,
      text_content: textContent,
      email_type: 'review_request',
      related_customer_id: customerId,
      metadata: {
        customer_id: customerId
      }
    });
  }

  /**
   * Cancel a queued email
   */
  static async cancelEmail(emailId: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('email_outbox')
        .update({
          status: 'cancelled',
          updated_at: new Date().toISOString()
        })
        .eq('id', emailId)
        .eq('status', 'pending'); // Only cancel pending emails

      if (error) {
        console.error('Error cancelling email:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error in cancelEmail:', error);
      throw error;
    }
  }

  /**
   * Get email statistics
   */
  static async getEmailStats(): Promise<{
    pending: number;
    sent: number;
    failed: number;
    testEmails: number;
  }> {
    try {
      const { data, error } = await supabase
        .from('email_outbox')
        .select('status, is_test_email');

      if (error) {
        console.error('Error fetching email stats:', error);
        return { pending: 0, sent: 0, failed: 0, testEmails: 0 };
      }

      type OutboxRow = { status: string; is_test_email?: boolean | null };
      type StatsAcc = { pending: number; sent: number; failed: number; cancelled: number; testEmails: number };
      const stats = (data as OutboxRow[]).reduce((acc: StatsAcc, email: OutboxRow) => {
        const key = email.status as keyof StatsAcc;
        if (key === 'pending' || key === 'sent' || key === 'failed' || key === 'cancelled') acc[key]++;
        if (email.is_test_email) acc.testEmails++;
        return acc;
      }, { pending: 0, sent: 0, failed: 0, cancelled: 0, testEmails: 0 });

      return {
        pending: stats.pending,
        sent: stats.sent,
        failed: stats.failed,
        testEmails: stats.testEmails
      };
    } catch (error) {
      console.error('Error in getEmailStats:', error);
      return { pending: 0, sent: 0, failed: 0, testEmails: 0 };
    }
  }
}